import React from 'react'
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { font, Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings } from '../../../types/types';
import { hexToRgba } from '../../../utils/helper';
import StyledInput from '../../StyledComponents/StyledInput';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';
import { Radio } from '@mui/material';
import LeftAlignIcon from '../../../assets/icons/LeftAlignIcon';
import CenterAlignIcon from '../../../assets/icons/CenterAlignIcon';
import RightAlignIcon from '../../../assets/icons/RightAlignIcon';
import StyledRadio from '../../StyledComponents/StyledRadio';
import AMSSelect from '../../AMSSelect';
import { Form } from 'react-bootstrap';
import LeftRightLayoutIcon from '../../../assets/icons/LeftRightLayoutIcon';
import RightLeftLayoutIcon from '../../../assets/icons/RightLeftLayoutIcon';
import CenterStackLayoutIcon from '../../../assets/icons/CenterStackLayoutIcon';

const SeparatorProperties = ({ updateTempSetting, tempSettings }: { updateTempSetting: (key: string, value: any) => void, tempSettings: TempSettings }) => {
    const { theme } = useTheme();
    const headingOptions = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
    const heightOptions = ['2px', '4px', '6px', '8px', '10px'];
    const separatorTypeOptions = ['solid', 'dashed'];
    const formatTypeLabel = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
    const textAlignmentOptions = [{ id: 'Left', icon: <LeftRightLayoutIcon /> }, { id: 'Right', icon: <RightLeftLayoutIcon /> }, { id: 'Center', icon: <CenterStackLayoutIcon /> }];
    const defaultOption = { value: 1, label: "Primary Menu (Header)" };

    return (
        <Container theme={theme}>
            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Appearance</StyledText>
            <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2 mt-4'>Color</StyledText>
            <Row style={{ alignItems: 'center' }}>
                <div style={{ position: 'relative', width: '105px', height: '42px' }}>
                    <input
                        type="color"
                        value={tempSettings.separatorColor || '#BABDC1'}
                        onChange={(e) => updateTempSetting('separatorColor', e.target.value)}
                        style={{
                            width: '105px',
                            height: '42px',
                            opacity: 0,
                            cursor: 'pointer',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 2,
                            borderRadius: '8px',
                            padding: 0,
                            margin: 0,
                            border: 'none',
                        }}
                    />

                    <div
                        style={{
                            width: '105px',
                            height: '42px',
                            border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                            borderRadius: '8px',
                            backgroundColor: tempSettings.separatorColor || '#BABDC1',
                            position: 'relative',
                            zIndex: 1,
                        }}
                    />
                </div>

                {/* Hex input */}
                <HexInput
                    theme={theme}
                    type="text"
                    value={tempSettings.separatorColor || '#BABDC1'}
                    onChange={(e) => updateTempSetting('separatorColor', e.target.value)}
                />
            </Row>

            <Section theme={theme}>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Separator Type</StyledText>
                <div>
                    <AMSSelect
                        showIcons
                        fontFamily={font.raleway}
                        staticOptions={separatorTypeOptions.map((type) => ({ value: type, label: formatTypeLabel(type) }))}
                        placeholder='Separator Type'
                        value={{
                            value: tempSettings.separatorType || 'solid',
                            label: formatTypeLabel(tempSettings.separatorType || 'solid')
                        }}
                        onChange={(option) => {
                            updateTempSetting('separatorType', (
                                option?.value !== undefined && option?.value !== null
                                    ? String(option.value)
                                    : 'solid'
                            ));
                        }}
                        defaultOptions
                    />
                </div>
            </Section>


            <Section theme={theme} className='mt-4 pt-2 mb-4'>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Separator Height</StyledText>
                <div>

                    <AMSSelect
                        showIcons
                        fontFamily={font.lato}
                        staticOptions={heightOptions.map((height) => ({ value: height.toLowerCase(), label: height.replace('px', ' PX') }))}
                        placeholder="Separator Height"
                        value={tempSettings.separatorHeight ? { value: tempSettings.separatorHeight, label: tempSettings.separatorHeight.replace('px', ' PX') } : undefined}
                        onChange={(option) => {
                            updateTempSetting('separatorHeight', (
                                option?.value !== undefined && option?.value !== null
                                    ? String(option.value)
                                    : undefined
                            ));
                        }}

                        defaultOptions
                    />
                </div>
            </Section>

        </Container>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column; 
    border-radius: 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    `

const Section = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;


const OptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
    color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    
    /* Remove margin */
    margin: 0;
    
    /* Default no border-radius */
    border-radius: 0;
    
    /* First button - rounded left corners */
    &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last button - rounded right corners */
    &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    /* Remove double borders between buttons */
    &:not(:first-child) {
        margin-left: -1px;
    }

    /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
    position: relative;
    z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

    &:hover {
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        z-index: 3; /* Higher z-index on hover */
    }

    /* Selected state styling */
    ${({ $isSelected, theme }) => $isSelected && `
        background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${theme.CorporateTheme.colors.accentOrange};
        color: ${theme.CorporateTheme.colors.accentOrange};
        font-weight: 600;
        z-index: 2;
    `}
`;

const ColorBox = styled.div<{ color: string }>`
  width: 90px;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  background: ${(p) => p.color};
`;

const HexInput = styled(Form.Control) <{ theme: Theme }>`
flex: 1;
  height: 42px;
  background: transparent;
    color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
`;

const ColorInput = styled.input`
  width: 105px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  cursor: pointer;
  padding: 0;
`;


const Row = styled.div`
  display: flex;
    gap: 10px;
  align-items: center;
  margin-bottom: 20px;
`;

const TextInput = styled.input<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    }
`;

const Select = styled.select<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    option {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
        color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    }
`;


const AlignmentCard = styled.button<{
    theme: any;
    $isSelected: boolean;
}>`
  display: flex;
  align-items: center;
    border:none;
  cursor: pointer;
    gap: 6px;
  background: #fff;

  transition: all 0.2s ease;


`;



export default SeparatorProperties
